import React, { useState, useRef, useEffect, useMemo, FC } from 'react';
import { Dropdown, Button, InputAdornment, Input, Icon } from 'tea-component';
import { newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import { useIntl, getLocale } from 'umi';
import classnames from 'classnames';
import './index.scss';

type RevenueRange = {
  min: number | undefined;
  max: number | undefined;
}
interface RevenueRangeProps {
  value: RevenueRange;
  title: string;
  width?: number;
  unitText?: string;
  clickSetDefault?: boolean;
  leftPlaceholder?: string;
  rightPlaceholder?: string;
  className?: string;
  onChange: (value: RevenueRange) => void;
}

const RevenueRange: FC<RevenueRangeProps> = (props) => {
  const { value = { min: '', max: '' }, onChange, unitText = '$', clickSetDefault = false,
    title, leftPlaceholder, rightPlaceholder, width, className } = props;

  const dropDown = useRef<HTMLDivElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const inputMinRef = useRef<HTMLInputElement>(null);
  const inputMaxRef = useRef<HTMLInputElement>(null);
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [isMinError, setIsMinError] = useState(false);
  const [isMaxError, setIsMaxError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // BMK最大数值限制999.99B
  const BMK = 999999999999;

  const handleClosePopover = () => {
    setIsOpen(false);
    setIsMaxError(false);
    setIsMinError(false);
  };

  const handleConfirm = () => {
    onChange({
      min: minValue ? Number(minValue) : undefined,
      max: maxValue ? Number(maxValue) : undefined,
    });
    handleClosePopover();
  };

  useEffect(() => {
    setMinValue(value?.min ? `${value?.min}` : value?.min === 0 ? '0' : '');
    setMaxValue(value?.max ? `${value?.max}` : value?.max === 0 ? '0' : '');

  }, [JSON.stringify(value), isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const closePopover = () => {
      setIsOpen(false);
      setIsMaxError(false);
      setIsMinError(false);
    };

    const handleOutSideClick = (e: Event) => {
      if (dropDown?.current?.contains(e.target as HTMLElement)) {
        return;
      }

      if (popover?.current?.contains(e.target as HTMLElement)) {
        return;
      }

      closePopover();
    };

    const handlePageScroll = () => {
      closePopover();
    };

    document.addEventListener('click', handleOutSideClick, true);
    window.addEventListener('scroll', handlePageScroll, true);

    return () => {
      document.removeEventListener('click', handleOutSideClick, true);
      window.removeEventListener('scroll', handlePageScroll, true);
    };
  }, [isOpen]);

  const handleInputChange = (val: string, type: 'min' | 'max', targetVal?: string) => {
    const value = val?.replace(/[,， ]/g, '');
    if (type === 'min') {
      setMinValue(value);

      if (!value) {
        setIsMinError(false);
        return;
      }
      if (!/^[0-9]*[\.]?[0-9]*$/.test(value)) {
        setIsMinError(true);
        // setIsMaxError(false);
        setErrorMsg('dbi_revenue_error_format_tip');
        return;
      }

      if (Number(value) > BMK) {
        setMinValue(`${BMK}`);
        // return;
      }

      const targetValue = Number(value) > BMK ? BMK : value;
      const maxVal = targetVal ? targetVal : maxValue;
      if (maxVal && Number(targetValue) > Number(maxVal)) {
        setIsMinError(true);
        setErrorMsg('dbi_min_greater_max');
        return;
      }

      setIsMinError(false);
      if (isMaxError) {
        handleInputChange(maxValue, 'max', value);
      }
    } else {
      setMaxValue(value);
      if (!value) {
        setIsMaxError(false);
        return;
      }
      if (!/^[0-9]*[\.]?[0-9]*$/.test(value)) {
        setIsMaxError(true);
        // setIsMinError(false);
        setErrorMsg('dbi_revenue_error_format_tip');
        return;
      }

      if (Number(value) > BMK) {
        setMaxValue(`${BMK}`);
      }
      const minVal = targetVal ? targetVal : minValue;
      if (minVal && Number(value) < Number(minVal)) {
        setIsMaxError(true);
        // setIsMinError(false);
        setErrorMsg('dbi_max_less_min');
        return;
      }

      setIsMaxError(false);

      if (isMinError) {
        handleInputChange(minValue, 'min', value);
      }
    }
  };

  const revenueRangeTitle = useMemo(() => {
    if (minValue && maxValue) {
      return `${newMathFormat(minValue, true)} ~ ${newMathFormat(
        maxValue,
        true,
      )}`;
    }

    if (minValue && !maxValue) {
      return `${newMathFormat(minValue, true)} ~ `;
    }

    if (!minValue && maxValue) {
      return `~ ${newMathFormat(maxValue, true)}`;
    }

    return <span className='dbi_revenue-range-select-input-range'>
      {formatMessage({ id: 'dbi_input_range' })}
    </span>;
  }, [minValue, maxValue, locale]);

  const handleDeleteRevenueRange = (type: 'min' | 'max') => {
    if (type === 'max') {
      setMaxValue('');
      setIsMaxError(false);
      if (inputMaxRef?.current) {
        inputMaxRef?.current?.focus();
      }

    } else {
      setMinValue('');
      setIsMinError(false);
      if (inputMinRef?.current) {
        inputMinRef?.current?.focus();
      }
    }
  };

  const handleInputClick = (type: 'min' | 'max') => {
    if (!clickSetDefault) return;
    if (type === 'max' && !maxValue) {
      handleInputChange('250000', 'max');
    }
    if (type === 'min' && !minValue) {
      handleInputChange('20000', 'min');
    }
  }

  return (
    <Dropdown
      ref={dropDown}
      className={classnames("select-item", "dbi-revenue-range-select-box", className)}
      overlayClassName="dbi-revenue-range-select-popover"
      // @ts-ignore
      onClick={() => setIsOpen(!isOpen)}
      open={isOpen}
      button={
        <div className="range-flex">
          <span className="dbi-revenue-range-select-title">
            {formatMessage({ id: title || 'dbi_revenue_range' })}
          </span>
          <span>{revenueRangeTitle}</span>
        </div>
      }
      style={{ width: width || 300 }}
      appearance="button"
      clickClose={false}
    >
      <div style={{ padding: 16 }} ref={popover}>
        <div className="dbi-revenue-range-select-input">
          <InputAdornment
            className="dbi-revenue-range-box"
            before={unitText}
            style={{ display: 'flex' }}
          >
            <Input
              ref={inputMinRef}
              value={minValue}
              placeholder={formatMessage({ id: leftPlaceholder || 'dbi_minimum' })}
              onClick={() => handleInputClick('min')}
              onChange={(val) => handleInputChange(val, 'min')}
              style={{ paddingRight: minValue ? 30 : 0 }}
              className={classnames([{ 'is-error': isMinError }, 'dbi-revenue-range-input'])}
            />
            {minValue ? (
              <Icon
                className="dbi-revenue-range-icon"
                type="dismiss"
                onClick={() => handleDeleteRevenueRange('min')}
              />
            ) : null}
          </InputAdornment>
          <span className="dbi-revenue-range-select-divide">-</span>
          <InputAdornment
            className="dbi-revenue-range-box"
            before={unitText}
            style={{ display: 'flex' }}
          >
            <Input
              ref={inputMaxRef}
              value={maxValue}
              onChange={(val) => handleInputChange(val, 'max')}
              onClick={() => handleInputClick('max')}
              placeholder={formatMessage({ id: rightPlaceholder || 'dbi_maximum' })}
              style={{ paddingRight: maxValue ? 30 : 0 }}
              className={classnames([{ 'is-error': isMaxError }, 'dbi-revenue-range-input'])}
            />
            {maxValue ? (
              <Icon
                className="dbi-revenue-range-icon"
                type="dismiss"
                onClick={() => handleDeleteRevenueRange('max')}
              />
            ) : null}
          </InputAdornment>
        </div>
        {isMinError || isMaxError ? (
          <div className="dbi-revenue-range-select-input-error">
            {formatMessage({ id: errorMsg })}
          </div>
        ) : null}

        <div className="dbi-revenue-btn">
          <Button
            type="primary"
            className="dbi-revenue-btn-left"
            onClick={handleConfirm}
            disabled={isMinError || isMaxError}
          >
            {formatMessage({ id: 'dbi_confirm' })}
          </Button>
          <Button
            className="dbi-revenue-btn-right"
            onClick={handleClosePopover}
          >
            {formatMessage({ id: 'dbi_cancel' })}
          </Button>
        </div>
      </div>
    </Dropdown>
  );
};

export default RevenueRange;
